import { useCallback, useMemo, useState } from "react";
import { ToggleLeft, ToggleRight } from "lucide-react";
import { Switch } from "@cieda/djayma-ui";

import { ADMIN_THEME } from "@/lib/admin-theme";

import { ScopeBadge } from "./badges";
import { FEATURE_FLAGS, FLAG_CATEGORIES } from "./constants";
import { loadFlags, saveFlags } from "./storage";
import type { FeatureFlag } from "./types";

// ---------------------------------------------------------------------------
// Tab 1: Feature Flags
// ---------------------------------------------------------------------------

export function FeatureFlagsTab() {
  const [flags, setFlags] = useState<Record<string, boolean>>(loadFlags);

  const handleToggle = useCallback((key: string, checked: boolean) => {
    setFlags((prev) => {
      const next = { ...prev, [key]: checked };
      saveFlags(next);
      return next;
    });
  }, []);

  const handleToggleCategory = useCallback(
    (categoryKey: string, enable: boolean) => {
      setFlags((prev) => {
        const next = { ...prev };
        for (const flag of FEATURE_FLAGS) {
          if (flag.category === categoryKey) {
            next[flag.key] = enable;
          }
        }
        saveFlags(next);
        return next;
      });
    },
    [],
  );

  const flagsByCategory = useMemo(() => {
    const grouped: Record<string, readonly FeatureFlag[]> = {};
    for (const cat of FLAG_CATEGORIES) {
      grouped[cat.key] = FEATURE_FLAGS.filter((f) => f.category === cat.key);
    }
    return grouped;
  }, []);

  return (
    <div className="space-y-6">
      {FLAG_CATEGORIES.map((category) => {
        const categoryFlags = flagsByCategory[category.key] ?? [];
        if (categoryFlags.length === 0) return null;

        const allEnabled = categoryFlags.every((f) => flags[f.key]);
        const noneEnabled = categoryFlags.every((f) => !flags[f.key]);
        const Icon = category.icon;

        return (
          <section key={category.key} className="space-y-3">
            {/* Category header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon
                  className="h-4 w-4"
                  style={{ color: ADMIN_THEME.accent.green }}
                />
                <h3 className="text-sm font-semibold uppercase tracking-wide text-stone-600">
                  {category.label}
                </h3>
                <span className="text-xs text-stone-400">
                  ({categoryFlags.filter((f) => flags[f.key]).length}/
                  {categoryFlags.length})
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => handleToggleCategory(category.key, true)}
                  disabled={allEnabled}
                  className="rounded px-2 py-1 text-xs font-medium text-stone-500 transition-colors hover:bg-stone-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ToggleRight className="mr-1 inline h-3 w-3" />
                  Tout activer
                </button>
                <button
                  type="button"
                  onClick={() => handleToggleCategory(category.key, false)}
                  disabled={noneEnabled}
                  className="rounded px-2 py-1 text-xs font-medium text-stone-500 transition-colors hover:bg-stone-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ToggleLeft className="mr-1 inline h-3 w-3" />
                  Tout désactiver
                </button>
              </div>
            </div>

            {/* Flag cards */}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {categoryFlags.map((flag) => (
                <div
                  key={flag.key}
                  className="rounded-xl border bg-white p-4 transition-shadow hover:shadow-sm"
                  style={{ borderColor: flags[flag.key] ? "rgba(45,90,61,0.3)" : "#e7e0d6" }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-stone-800">
                          {flag.name}
                        </span>
                        <ScopeBadge scope={flag.scope} />
                      </div>
                      <p className="text-xs leading-relaxed text-stone-500">
                        {flag.description}
                      </p>
                    </div>
                    <Switch
                      checked={!!flags[flag.key]}
                      onCheckedChange={(checked) =>
                        handleToggle(flag.key, checked)
                      }
                      className="shrink-0 data-[state=checked]:bg-[#2D5A3D]"
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
